import { useAuthStore } from "@/features/auth/useAuth";
import type { User } from "@/types/api";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/DeleteOutline";
import EditIcon from "@mui/icons-material/Edit";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
} from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { usersApi } from "./api";
import { usersKeys } from "./queryKeys";
import { UserDialog } from "./UserDialog";

export function UsersPage() {
  const qc = useQueryClient();
  const { user: currentUser } = useAuthStore();
  const [editing, setEditing] = useState<User | null>(null);
  const [open, setOpen] = useState(false);
  const [deleting, setDeleting] = useState<User | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(30);

  const list = useQuery({ queryKey: usersKeys.lists(), queryFn: usersApi.list });
  const remove = useMutation({
    mutationFn: usersApi.remove,
    onSuccess: () => qc.invalidateQueries({ queryKey: usersKeys.lists() }),
  });

  const rows = list.data ?? [];
  const paginatedRows = rows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  function openCreate() {
    setEditing(null);
    setOpen(true);
  }

  function openEdit(u: User) {
    setEditing(u);
    setOpen(true);
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%", p: { xs: 1, sm: 2 } }}>
      <TableContainer sx={{ flex: 1, overflowX: "auto" }}>
        <Table size="small" stickyHeader sx={{ minWidth: { xs: 520, sm: 0 } }}>
          <TableHead>
            <TableRow>
              <TableCell>FIRST NAME</TableCell>
              <TableCell>LAST NAME</TableCell>
              <TableCell sx={{ display: { xs: "none", sm: "table-cell" } }}>ROLE</TableCell>
              <TableCell sx={{ display: { xs: "none", md: "table-cell" } }}>EMAIL</TableCell>
              <TableCell align="right">ACTIONS</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedRows.map((u) => (
              <TableRow key={u.id} hover>
                <TableCell>{u.firstName}</TableCell>
                <TableCell>{u.lastName}</TableCell>
                <TableCell sx={{ display: { xs: "none", sm: "table-cell" } }}>
                  {u.role ?? (u.owner ? "user" : "admin")}
                </TableCell>
                <TableCell sx={{ display: { xs: "none", md: "table-cell" } }}>{u.email}</TableCell>
                <TableCell align="right">
                  <Tooltip title="Edit">
                    <IconButton
                      color="primary"
                      size="small"
                      onClick={() => openEdit(u)}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip
                    title={u.id === currentUser?.id ? "Cannot delete your own account" : "Delete"}
                  >
                    <span>
                      <IconButton
                        color="error"
                        size="small"
                        disabled={u.id === currentUser?.id}
                        onClick={() =>
                          setDeleting(u)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-end",
          flexWrap: "wrap",
          px: 1,
        }}
      >
        <TablePagination
          component="div"
          count={rows.length}
          page={page}
          rowsPerPage={rowsPerPage}
          rowsPerPageOptions={[10, 30, 50]}
          onPageChange={(_, newPage) => setPage(newPage)}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
        />
        <Tooltip title="Add user">
          <IconButton color="primary" onClick={openCreate} sx={{ ml: 1 }}>
            <AddIcon />
          </IconButton>
        </Tooltip>
      </Box>

      <UserDialog open={open} user={editing} onClose={() => setOpen(false)} />

      <Dialog open={deleting !== null} onClose={() => setDeleting(null)}>
        <DialogTitle>Delete user</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete{" "}
            <strong>{deleting?.firstName} {deleting?.lastName}</strong>? This action cannot be
            undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleting(null)}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            disabled={remove.isPending}
            onClick={() => {
              if (deleting) remove.mutate(deleting.id, { onSuccess: () => setDeleting(null) });
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
